import React from "react";
import { AnimatePresence, motion, useTransform } from "framer-motion";
import { ContainerScroll } from "../Components/ui/container-scroll-animation";
import { SunscreenedAppUi } from "../Components/ui/sunscreened-app-ui";
import { ScientificFoundationSlideshow } from "../Components/blocks/scientific-foundation-slideshow";
import pressReleasePhoto from "../assets/press/press-release-photo.png";

function AmbientOrb({ className }) {
  return (
    <div
      className={`pointer-events-none absolute rounded-full blur-[120px] opacity-30 ${className}`}
      aria-hidden
    />
  );
}

function Grain() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-40 opacity-[0.028]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundRepeat: "repeat",
        backgroundSize: "128px",
      }}
      aria-hidden
    />
  );
}

function Ticker({ items }) {
  const doubled = [...items, ...items];
  return (
    <div className="relative overflow-hidden py-5 border-y border-white/[0.06]">
      <div className="flex w-max animate-[ticker_22s_linear_infinite]">
        {doubled.map((item, i) => (
          <span
            key={i}
            className="flex items-center gap-6 px-8 text-[11px] font-medium uppercase tracking-[0.18em] text-white/30 whitespace-nowrap"
          >
            <span className="inline-block h-[1px] w-5 bg-orange-400/40" />
            <span className={item.highlight ? "text-orange-300/60" : ""}>{item.text}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const powerfulFeatures = [
  {
    title: "Computer Vision Analysis",
    body: "Advanced AI analyzes your skin using the Melanin Index, Erythema Index, and Individual Typology Angle to detect early signs of sun damage.",
  },
  {
    title: "Real-Time UV Tracking",
    body: "Integrates live weather data and UV index information to provide accurate, location-based sun exposure monitoring.",
  },
  {
    title: "Personalized Recommendations",
    body: "Receive custom SPF suggestions and safe tanning times based on your skin type, location, and current UV levels.",
  },
  {
    title: "Progress Visualization",
    body: "Track your skin health journey with beautiful data visualizations showing changes over time and exposure patterns.",
  },
  {
    title: "Smart Alerts",
    body: "Get instant notifications when excessive exposure is detected or when skin redness levels indicate it's time to seek shade.",
  },
  {
    title: "Skin Health Insights",
    body: "Understand your skin's response to sun exposure with scientific measurements and actionable health recommendations.",
  },
];

export default function Home() {
  const [showIntro, setShowIntro] = React.useState(true);
  const [typedLineOne, setTypedLineOne] = React.useState("");
  const [typedLineTwo, setTypedLineTwo] = React.useState("");

  React.useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setTypedLineOne("The Sun is Dangerous");
      setTypedLineTwo("Use SunScreened!");
      const done = window.setTimeout(() => setShowIntro(false), 700);
      return () => window.clearTimeout(done);
    }

    const lineOne = "The Sun is Dangerous";
    const lineTwo = "Use SunScreened!";
    const START_DELAY_MS = 220;
    const PAUSE_BETWEEN_LINES_MS = 220;
    const PAUSE_BEFORE_DISMISS_MS = 420;
    const SPEED_LINE_ONE = { min: 55, max: 95 };
    const SPEED_LINE_TWO = { min: 45, max: 85 };
    let i = 0;
    let j = 0;
    let timers = [];

    const rand = (min, max) => Math.floor(min + Math.random() * (max - min + 1));

    const typeLineOne = () => {
      i += 1;
      setTypedLineOne(lineOne.slice(0, i));
      if (i >= lineOne.length) {
        timers.push(window.setTimeout(typeLineTwo, PAUSE_BETWEEN_LINES_MS));
        return;
      }
      const nextDelay = rand(SPEED_LINE_ONE.min, SPEED_LINE_ONE.max);
      timers.push(window.setTimeout(typeLineOne, nextDelay));
    };

    const typeLineTwo = () => {
      j += 1;
      setTypedLineTwo(lineTwo.slice(0, j));
      if (j >= lineTwo.length) {
        timers.push(window.setTimeout(() => setShowIntro(false), PAUSE_BEFORE_DISMISS_MS));
        return;
      }
      const nextDelay = rand(SPEED_LINE_TWO.min, SPEED_LINE_TWO.max);
      timers.push(window.setTimeout(typeLineTwo, nextDelay));
    };

    timers.push(window.setTimeout(typeLineOne, START_DELAY_MS));

    return () => {
      timers.forEach((t) => {
        window.clearTimeout(t);
        window.clearInterval(t);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <style>{`
        @keyframes ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        ::selection { background: rgba(249,115,22,0.3); color: white; }
      `}</style>

      <Grain />

      <AmbientOrb className="top-[-220px] right-[-120px] h-[720px] w-[720px] bg-orange-500/20" />
      <AmbientOrb className="top-[42vh] left-[-220px] h-[520px] w-[520px] bg-orange-600/12" />

      <AnimatePresence>
        {showIntro ? (
          <motion.div
            key="intro"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] grid place-items-center bg-black"
          >
            <div className="mx-auto w-full max-w-4xl px-6 text-center">
              <div className="text-3xl font-semibold tracking-tight text-white md:text-6xl">
                <span className="whitespace-pre-wrap">{typedLineOne}</span>
                <span className="inline-block w-[0.6ch] animate-pulse text-white/80">|</span>
              </div>
              <div className="mt-4 text-3xl font-semibold tracking-tight md:text-6xl">
                <span className="bg-gradient-to-r from-orange-300 via-amber-200 to-white bg-clip-text text-transparent whitespace-pre-wrap">
                  {typedLineTwo}
                </span>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <section id="home" className="overflow-hidden pt-10 md:pt-14">
        <ContainerScroll
          titleComponent={
            <div className="mx-auto max-w-5xl px-6">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.24em] text-orange-300">
                AI-Powered Skin Protection
              </p>
              <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-white">
                The Sun is Dangerous
                <br />
                <span className="bg-gradient-to-r from-orange-300 via-amber-200 to-white bg-clip-text text-transparent">
                  Use SunScreened!
                </span>
              </h1>
              <p className="mt-5 max-w-3xl mx-auto text-lg text-white/80 leading-relaxed">
                <span className="font-semibold text-white">Your Smart UV Protection</span>
                <br />
                Calculate safe tanning times, track skin changes, and receive personalized alerts using advanced computer
                vision and real-time UV data.
              </p>
            </div>
          }
          overlayComponent={({ scrollYProgress }) => {
            const leftYTwo = useTransform(scrollYProgress, [0, 1], [26, -4]);
            const leftBlurTwo = useTransform(scrollYProgress, [0.06, 0.42], [12, 0]);
            const leftFilterTwo = useTransform(leftBlurTwo, (v) => `blur(${v}px)`);

            const leftYThree = useTransform(scrollYProgress, [0, 1], [32, 0]);
            const leftBlurThree = useTransform(scrollYProgress, [0.16, 0.56], [12, 0]);
            const leftFilterThree = useTransform(leftBlurThree, (v) => `blur(${v}px)`);

            return (
              <>
                <div className="pointer-events-none absolute left-6 top-1/2 z-20 hidden w-[46%] max-w-xl -translate-y-1/2 md:block">
                  <motion.div
                    style={{ y: leftYTwo, filter: leftFilterTwo }}
                    className="relative flex h-[34rem] flex-col justify-between [transform:perspective(900px)_rotateY(10deg)_rotateZ(-6deg)]"
                  >
                    {[
                      { title: "UV Monitoring", body: "Live weather and UV index tracking" },
                      { title: "Skin Analysis", body: "Track changes with camera-based AI" },
                      { title: "Safe Tanning", body: "Calculate personalized exposure times" },
                      { title: "Smart Alerts", body: "Get notified of excessive exposure" },
                    ].map((item, idx) => (
                      <div
                        key={item.title}
                        className={`rounded-2xl border p-4 shadow-[0_24px_70px_-55px_rgba(0,0,0,0.9)] backdrop-blur-xl [transform:translateX(var(--x))_rotateZ(var(--r))] ${
                          idx === 3
                            ? "border-orange-400/20 bg-orange-400/10"
                            : "border-white/10 bg-black/55"
                        }`}
                        style={{
                          // Spread + “angle with the phone”
                          // small horizontal offsets + subtle per-card rotation
                          "--x": idx === 0 ? "-18px" : idx === 1 ? "-6px" : idx === 2 ? "6px" : "18px",
                          "--r": idx === 0 ? "-1.5deg" : idx === 1 ? "-0.75deg" : idx === 2 ? "0.75deg" : "1.5deg",
                        }}
                      >
                        <p
                          className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${
                            idx === 3 ? "text-orange-200" : "text-orange-300/90"
                          }`}
                        >
                          {item.title}
                        </p>
                        <p className="mt-2 text-[13px] leading-relaxed text-white/80">{item.body}</p>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </>
            );
          }}
        >
          <div className="h-full w-full flex items-center justify-end px-2 md:px-6 lg:pr-2">
            <div className="h-full w-full max-w-[620px] ml-auto">
              <SunscreenedAppUi />
            </div>
          </div>
        </ContainerScroll>
      </section>

      <Ticker
        items={[
          { text: "AI-powered skin protection", highlight: false },
          { text: "Live UV tracking", highlight: true },
          { text: "Computer vision analysis", highlight: false },
          { text: "Personalized exposure limits", highlight: true },
          { text: "Congressional App Challenge Winner 2025", highlight: false },
          { text: "Available on iOS", highlight: false },
        ]}
      />

      <main className="mx-auto max-w-7xl space-y-24 px-6 pb-28">
        <section className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-orange-300">Powerful Features</p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight">Technology Meets Sun Safety</h2>
            <p className="mt-4 max-w-xl text-white/70">
              Combining engineering, health technology, and AI to help you make safer choices for your skin
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {powerfulFeatures.map((feature) => (
              <div key={feature.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
                <p className="text-lg font-semibold text-white">{feature.title}</p>
                <p className="mt-3 text-white/70 leading-relaxed">{feature.body}</p>
              </div>
            ))}
          </div>
        </section>

        <ScientificFoundationSlideshow />

        <section className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 p-8 md:p-10">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_50%_at_20%_20%,rgba(249,115,22,0.16),transparent_60%),radial-gradient(ellipse_70%_50%_at_80%_70%,rgba(244,63,94,0.10),transparent_60%)]" />

          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-orange-300">Why it matters</p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight">Make sun safety effortless</h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              Sunscreened turns invisible UV risk into clear, actionable signals — so you know when to protect, when to
              pause, and how your skin is responding.
            </p>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {[
              {
                eyebrow: "Real-time context",
                title: "UV + weather, fused",
                body: "Location-based UV index and conditions give you the right signal for the right moment.",
              },
              {
                eyebrow: "Prevention first",
                title: "Alerts before damage",
                body: "Smart nudges help you step into shade before excessive exposure or redness escalation.",
              },
              {
                eyebrow: "Personal insight",
                title: "Skin response, quantified",
                body: "Science-backed measurements translate your exposure into understandable skin health insights.",
              },
            ].map((card) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-3xl border border-white/10 bg-black/35 p-6 shadow-[0_30px_80px_-50px_rgba(0,0,0,0.9)] backdrop-blur-xl"
              >
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-orange-300/90">{card.eyebrow}</p>
                <p className="mt-3 text-xl font-semibold text-white">{card.title}</p>
                <p className="mt-3 text-white/70 leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="grid gap-8 lg:grid-cols-[1fr_1fr] items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-orange-300">
              Congressional App Challenge Winner 2025
            </p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight">SunScreened Wins Congressional App Challenge</h2>
            <p className="mt-4 text-white/70">
              Recognized by U.S. Rep. Kathy Castor (FL-14) for excellence in STEM innovation
            </p>
            <blockquote className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6 text-white/80">
              &quot;I am proud that this app will represent the 14th District, and I know it will help not only our
              neighbors in the Sunshine State but Americans across the country.&quot;
              <footer className="mt-4 text-sm text-white/55">— U.S. Rep. Kathy Castor (FL-14)</footer>
            </blockquote>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-300">Recognition</p>
                <p className="mt-2 text-white/70">
                  Top prize winner from Robinson High School, representing the 14th District of Florida at the U.S. Capitol
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-300">Impact</p>
                <p className="mt-2 text-white/70">
                  Created with JavaScript and AI image processing to help Americans understand UV ray harms and sunscreen importance
                </p>
              </div>
            </div>
            <a
              href="https://castor.house.gov/news/documentsingle.aspx?DocumentID=405108"
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex rounded-xl border border-white/15 bg-white/5 px-5 py-3 font-semibold text-white hover:bg-white/10"
            >
              Read Full Press Release
            </a>
          </div>
          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/5">
            <img src={pressReleasePhoto} alt="Press release photo" className="h-[520px] w-full object-cover" draggable={false} />
          </div>
        </section>

        <section
          id="download"
          className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-orange-500/20 via-white/5 to-white/0 p-8 md:p-10"
        >
          <h2 className="text-4xl font-semibold tracking-tight">Ready to Protect Your Skin?</h2>
          <p className="mt-4 max-w-3xl text-white/70">
            Join thousands of users who trust SunScreened for smarter, safer sun exposure
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="https://apps.apple.com/us/app/sunscreened/id6756286578"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-xl bg-orange-400 px-6 py-3 font-semibold text-black transition-colors hover:bg-orange-300"
            >
              Download on the App Store
            </a>
          </div>
          <p className="mt-4 text-sm font-medium text-white/60">Rated 5/5 on the App Store.</p>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-black/60">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-[1.2fr_0.8fr_0.8fr_1fr]">
          <div>
            <p className="text-lg font-semibold tracking-tight text-white">SunScreened Logo</p>
            <p className="mt-3 text-2xl font-semibold text-white">SunScreened</p>
            <p className="mt-4 max-w-sm text-white/65">
              Empowering you to enjoy the sun safely through AI-powered skin monitoring and personalized UV protection.
            </p>
          </div>
          <div>
            <p className="font-semibold text-white">Quick Links</p>
            <div className="mt-4 space-y-3 text-white/65">
              <a href="#home" className="block hover:text-white">Home</a>
              <a href="#download" className="block hover:text-white">Download</a>
              <a href="#" className="block hover:text-white">Articles</a>
              <a href="#" className="block hover:text-white">Support</a>
            </div>
          </div>
          <div>
            <p className="font-semibold text-white">Legal</p>
            <div className="mt-4 space-y-3 text-white/65">
              <a href="#" className="block hover:text-white">
                Privacy Policy
              </a>
              <a href="#" className="block hover:text-white">
                Terms &amp; Conditions
              </a>
            </div>
          </div>
          <div>
            <p className="font-semibold text-white">Get in Touch</p>
            <p className="mt-4 text-white/65">
              Have questions about sun safety or our technology?
            </p>
          </div>
        </div>
        <div className="border-t border-white/10 px-6 py-5 text-center text-sm text-white/45">
          © 2025 SunScreened. Combining engineering, health technology, and AI for safer sun exposure.
        </div>
      </footer>
    </div>
  );
}
